// tslint:disable:max-line-length
import { AccordionItem, Heading } from '@mm/components';
import { ArrowStatefulIcon } from '@mm/icons';
import {
  FilterItem,
  FILTER_TYPES,
  TagFilterItem,
} from '@mm/state/model/AudiencesModel';
import classNames from 'classnames';
import React from 'react';
import { EUMembership } from '../../../../../contacts/components/FilterModals/EUMembership/EUMembership';
import { PlatformsFilterModal } from '../../../../../contacts/components/FilterModals/platformsFilterModal/platformsFilterModal';
import { ReachableModal } from '../../../../../contacts/components/FilterModals/reachableModal/reachableModal';
import { TagsModal } from '../../../../../contacts/components/FilterModals/tagsModal/tagsModal';
import VariableValuesModal from '../../../../../contacts/components/FilterModals/variableValuesModal/variableValuesModal';
import {
  LocalFilterItem,
  LocalSystemFilterItem,
  LocalVariablesValueFilterItem,
} from '../../../../../contacts/sharedFilterTypes';
import { LocalTagFilterItem } from '../AudienceActiveFilters/AudienceActiveFilters';
import { useAudienceFiltersHandlers } from '../use-audience-filters-handlers';
import './AudienceFiltersMenu.pcss';

type Props = {
  className?: string;
  audienceId: number;
  onFilterAddSuccess?: (filter: FilterItem | TagFilterItem) => void;
  onFilterAddCancel?: () => void;
  filterToEdit?: LocalFilterItem | LocalTagFilterItem;
};

// tslint:disable-next-line: cyclomatic-complexity
export const AudienceFiltersMenu = (props: Props) => {
  const {
    className,
    audienceId,
    onFilterAddSuccess = () => {},
    onFilterAddCancel = () => {},
    filterToEdit,
  } = props;

  const {
    addTagFilter,
    addFilter,
    updateFilterItemById,
    updateTagFilterItemById,
  } = useAudienceFiltersHandlers(audienceId);

  const onFilterAdd = (filter?: FilterItem, tagFilter?: TagFilterItem) => {
    if (filter) {
      addFilter(filter);
      onFilterAddSuccess(filter);
    }

    if (tagFilter) {
      addTagFilter(tagFilter);
      onFilterAddSuccess(tagFilter);
    }
  };

  const onCancel = () => onFilterAddCancel();

  const isEditMode = filterToEdit;

  const isTag = filterToEdit?.filterType === FILTER_TYPES.tagsFilters;
  const isReachable = filterToEdit?.filterType === FILTER_TYPES.reachable;
  const isVariable = filterToEdit?.filterType === FILTER_TYPES.variableValues;
  const isPlatform = filterToEdit?.filterType === FILTER_TYPES.platforms;
  const isEULimits = filterToEdit?.filterType === FILTER_TYPES.euLimits;

  const containerClassName = classNames(className, 'audience-filters-menu');

  return (
    <div className={containerClassName}>
      <div className="audience-filters-menu__back-btn" onClick={onCancel}>
        <ArrowStatefulIcon direction="left" />
        <Heading size={4} text="Back" />
      </div>

      {(!isEditMode || (isEditMode && isTag)) && (
        <AccordionItem
          title="Filter by tag"
          openOnMount={isTag}
          arrowClassName="audience-filters-menu__accordion-arrow"
        >
          <TagsModal
            className="audience-filters-menu__filter"
            onFinish={onFilterAdd}
            onUpdateFilter={updateTagFilterItemById}
            toggle={onCancel}
            nonModalUse
            selectedTagId={
              isTag ? (filterToEdit?.filterId as number) : undefined
            }
            selectedTag={isTag ? (filterToEdit as TagFilterItem) : undefined}
          />
        </AccordionItem>
      )}

      {(!isEditMode || (isEditMode && isVariable)) && (
        <AccordionItem
          title="Filter by attribute"
          openOnMount={isVariable}
          arrowClassName="audience-filters-menu__accordion-arrow"
        >
          <VariableValuesModal
            onFinish={onFilterAdd}
            onUpdateFilter={updateFilterItemById}
            toggle={onCancel}
            nonModalUse
            selectedFilterId={
              isVariable ? Number(filterToEdit?.filterId) : undefined
            }
            selectedFilter={
              isVariable
                ? (filterToEdit as LocalVariablesValueFilterItem)
                : undefined
            }
          />
        </AccordionItem>
      )}

      {(!isEditMode || (isEditMode && isReachable)) && (
        <AccordionItem
          title="Filter by active within 24 hours"
          arrowClassName="audience-filters-menu__accordion-arrow"
          openOnMount={isReachable}
        >
          <ReachableModal
            className="audience-filters-menu__filter"
            onFinish={onFilterAdd}
            onUpdateFilter={updateFilterItemById}
            toggle={onCancel}
            nonModalUse
            selectedFilterId={
              isReachable ? Number(filterToEdit?.filterId) : undefined
            }
            selectedFilter={
              isReachable ? (filterToEdit as LocalSystemFilterItem) : undefined
            }
          />
        </AccordionItem>
      )}

      {(!isEditMode || (isEditMode && isPlatform)) && (
        <AccordionItem
          title="Filter by Platform"
          openOnMount={isPlatform}
          arrowClassName="audience-filters-menu__accordion-arrow"
        >
          <PlatformsFilterModal
            className="audience-filters-menu__filter--platforms"
            onFinish={onFilterAdd}
            onUpdateFilter={updateFilterItemById}
            toggle={onCancel}
            nonModalUse
            selectedFilterId={
              isPlatform ? Number(filterToEdit?.filterId) : undefined
            }
            selectedFilter={
              isPlatform ? (filterToEdit as LocalSystemFilterItem) : undefined
            }
          />
        </AccordionItem>
      )}

      {(!isEditMode || (isEditMode && isEULimits)) && (
        <AccordionItem
          title="Filter by EU Membership"
          arrowClassName="audience-filters-menu__accordion-arrow"
          openOnMount={isEULimits}
          includeTooltip={true}
        >
          <EUMembership
            className="audience-filters-menu__filter"
            onFinish={onFilterAdd}
            onUpdateFilter={updateFilterItemById}
            toggle={onCancel}
            selectedFilterId={
              isEULimits ? Number(filterToEdit?.filterId) : undefined
            }
            selectedFilter={
              isEULimits ? (filterToEdit as LocalSystemFilterItem) : undefined
            }
          />
        </AccordionItem>
      )}
    </div>
  );
};
